import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Button, Input, Select } from "antd";
import CircularProgress from "@mui/material/CircularProgress";

import { postRestock } from "../../../reducer/ingredientsReducer";
import { getAllIngredients } from "../../../reducer/ingredientsReducer";
import { getAllItems } from "../../../reducer/getItemReducer";
import { updateActiveView } from "../../../reducer/manageStocksReducer";
import { CURRENCY_FORMAT } from "../../../utils/helper";
import { HEADER_ITEMS } from "../utils";
import { TranslateText } from "../../../utils/translation";

import { ReactComponent as CrossIcon } from "../../../assets/images/svgs/CrossIcon2.svg";
import { ReactComponent as EmptyPreview } from "../../../assets/icons/emptyReports.svg";

const Restock = () => {
  const { hotelDetails } = useSelector((store) => store.login);
  const { selectedPos } = useSelector((store) => store.manageStocks);
  const { data: items, loading: itemsLoading } = useSelector(
    (store) => store.getItems
  );
  const { data: ingredients, loading: ingredientsLoading } = useSelector(
    (store) => store.ingredients
  );

  const [restockItems, setRestockItems] = useState({});
  const [filteredList, setFilteredist] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const isRestaurant = selectedPos?.type === "Restaurant";

  // if pos type is restaurant, then show list of ingredients, otherwise show items
  useEffect(() => {
    const isRestaurant = selectedPos?.type === "Restaurant";
    let updatedList = [];
    updatedList = isRestaurant ? ingredients : items;
    const allCategories = updatedList?.map(
      (obj) => obj[isRestaurant ? "type" : "category"]
    );
    const updatedCategories = [...new Set(allCategories)]?.map((category) => {
      return { value: category, label: category };
    });
    setList(updatedList);
    setCategories(updatedCategories);
  }, [ingredients, items, selectedPos]);

  // effect to filter out list of items based on searcha and category filter
  useEffect(() => {
    const isRestaurant = selectedPos?.type === "Restaurant";

    const updatedList = list
      ?.filter((obj) => {
        const category = isRestaurant ? obj?.type : obj?.category;
        return !categoryFilter ? true : categoryFilter === category;
      })
      ?.filter((obj) => obj?.name?.toLowerCase()?.includes(searchString));
    setFilteredist(updatedList);
  }, [list, searchString, categoryFilter]);

  const handleAdd = (obj) => {
    setRestockItems({
      ...restockItems,
      [obj?.id]: { ...obj, addedQuantity: 1 },
    });
  };

  const handleChangeQuantity = (id, method, value) => {
    let updatedRestockItems = JSON.parse(JSON.stringify(restockItems));

    if (method === "increase") {
      updatedRestockItems[id].addedQuantity += 1;
    } else if (method === "decrease") {
      // check if current addedQuantity is equal to 1, if so, remove it from the list
      if (updatedRestockItems[id].addedQuantity <= 1) {
        delete updatedRestockItems[id];
      } else {
        updatedRestockItems[id].addedQuantity -= 1;
      }
    } else if (method === "delete") {
      delete updatedRestockItems[id];
    } else {
      if (isNaN(value)) return;
      updatedRestockItems[id].addedQuantity = value;
    }
    setRestockItems(updatedRestockItems);
  };

  const handleSave = async () => {
    const isRestaurant = selectedPos?.type === "Restaurant";
    const key = isRestaurant ? "ingredientsMap" : "itemsMap";
    let value = {};
    Object.keys(restockItems)?.forEach((key) => {
      value[key] = restockItems[key]["addedQuantity"];
    });

    const payload = {
      hotelId: hotelDetails?.id,
      posId: selectedPos?.id,
      [key]: value,
    };

    setLoading(true);
    const res = await dispatch(postRestock(payload));
    setLoading(false);

    if (res?.payload?.status === 200) {
      setRestockItems({});
      setSearchString("");

      // call ingredient or item api to show updated list of data
      const selectedPosType = selectedPos?.type;
      const payload = { hotelId: hotelDetails?.id, posId: selectedPos?.id };

      if (selectedPosType === "Restaurant") {
        dispatch(getAllIngredients(payload));
      } else {
        dispatch(getAllItems(payload));
      }

      dispatch(updateActiveView("list"));
    }
  };

  if (itemsLoading || ingredientsLoading) {
    return (
      <div className="w-full h-full pr-[50px] pl-[20px] pb-[20px] bg-[#fff] flex items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex gap-[25px]">
      <div className="w-[65%] rounded-[10px] bg-[#fff]">
        <div className="w-full flex px-[20px] items-center justify-center gap-[10px] py-[20px]">
          <Input
            className="h-[36px] w-[80%]"
            placeholder={isRestaurant ? "Search ingredient" : "Seach item"}
            value={searchString}
            onChange={(e) => setSearchString(e.target.value.toLowerCase())}
          />
          <Select
            className="h-[36px] w-[20%]"
            style={{ width: 155 }}
            value={categoryFilter}
            placeholder={"Select Category"}
            onChange={(value) => {
              setSearchString("");
              setCategoryFilter(value);
            }}
            options={[{ value: "", label: "All Categories" }, ...categories]}
          />
        </div>
        <div className="w-full h-[60px] bg-[#fff] border-b-[#7E8082] border-b-[1px] rounded-t-[10px] flex items-center">
          {HEADER_ITEMS?.filter((_, idx) => idx < 5)?.map((heading, idx) => {
            if (idx === 0 && isRestaurant)
              heading = (
                <TranslateText
                  textKey={"ingredient_name"}
                  namespace="manageStocks"
                />
              );
            if (idx === 4)
              heading = (
                <TranslateText
                  textKey={"add_quantity"}
                  namespace="manageStocks"
                />
              );
            return (
              <div className="w-[20%] h-full flex items-center pl-[20px]">
                <span className="font-medium text-gray-500 uppercase tracking-wider leading-[1rem] text-[0.9rem]">
                  {heading}
                </span>
              </div>
            );
          })}
        </div>
        <div className="max-h-[62vh] w-full relative overflow-y-scroll">
          {Array.isArray(filteredList) && filteredList?.length ? (
            filteredList?.map((obj, idx) => {
              const isLastRow = idx === list?.length - 1;

              const coulmnsData = {
                id: obj?.id,
                name: obj?.name,
                category: isRestaurant ? obj?.type : obj?.category,
                unit: obj?.unit,
                price: obj?.price,
                quantity: isRestaurant ? obj?.stockCount : obj?.stock,
                total:
                  obj?.price * (isRestaurant ? obj?.stockCount : obj?.stock),
              };
              const { id, name, category, unit, price, quantity, total } =
                coulmnsData;

              // finding if the current item/ingredient is added in restock list or not
              const isRestock = restockItems?.hasOwnProperty(id);

              return (
                <div
                  key={id}
                  className={`w-full h-[45px] bg-[#fff] flex items-center border-b-[#00000040] border-b-[1px] ${
                    isLastRow && "rounded-b-[10px]"
                  }`}
                >
                  <div className="w-[20%] h-full flex items-center pl-[20px]">
                    <span className="font-nunito font-[500] text-[15px] leading-[21px] text-[#4D4D4D]">
                      {Boolean(name) ? name : "-"}
                    </span>
                  </div>
                  <div className="w-[20%] h-full flex items-center pl-[20px]">
                    <span className="font-nunito font-[500] text-[15px] leading-[21px] text-[#4D4D4D]">
                      {Boolean(category) ? category : "-"}
                    </span>
                  </div>
                  <div className="w-[20%] h-full flex items-center pl-[20px]">
                    <span className="font-nunito font-[500] text-[15px] leading-[21px] text-[#4D4D4D]">
                      {Boolean(unit) ? unit : "-"}
                    </span>
                  </div>
                  <div className="w-[20%] h-full flex items-center pl-[20px]">
                    <span className="font-nunito font-[500] text-[15px] leading-[21px] text-[#4D4D4D]">
                      {Boolean(price) ? CURRENCY_FORMAT(price) : 0}
                    </span>
                  </div>

                  <div className="w-[20%] h-full flex items-center pl-[20px]">
                    {isRestock ? (
                      <div className="flex gap-[5px]">
                        <Button
                          className="w-[20px] h-[20px] border-[1.3px] border-[#2F80ED] text-[12px] leading-[18px] text-[#2F80ED] rounded-[5px] flex items-center justify-center cursor-pointer"
                          onClick={() => handleChangeQuantity(id, "decrease")}
                        >
                          -
                        </Button>
                        <Input
                          className="w-[auto] h-[20px] border-[1.3px] border-[#2F80ED] text-[12px] leading-[18px]
                           text-[#2F80ED] rounded-[5px] text-center"
                          value={restockItems?.[id]?.addedQuantity}
                          onChange={(e) =>
                            handleChangeQuantity(id, "input", e.target.value)
                          }
                        />
                        <Button
                          className="w-[20px] h-[20px] border-[1.3px] border-[#2F80ED] text-[12px] leading-[18px] text-[#2F80ED] rounded-[5px] flex items-center justify-center cursor-pointer"
                          onClick={() => handleChangeQuantity(id, "increase")}
                        >
                          +
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="border-[#2F80ED] cursor-pointer px-[5px] font-[300] font-nunito border-[1px] text-[#2F80ED] rounded-[5px]"
                        onClick={() => handleAdd(coulmnsData)}
                      >
                        <TranslateText
                          textKey={"add"}
                          namespace="manageStocks"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : searchString || categoryFilter ? (
            <p className="ml-[20px] pt-[150px] font-[300] font-nunito">
              {`No ${
                isRestaurant ? "ingredients" : "items"
              } present for the selected filters. Please click here to `}
              <span
                className="cursor-pointer text-[#0000EE]"
                onClick={() => {
                  setSearchString("");
                  setCategoryFilter("");
                }}
              >
                <TranslateText textKey={"reset"} namespace="manageStocks" />
              </span>{" "}
              <TranslateText textKey={"filters"} namespace="manageStocks" />
            </p>
          ) : (
            <p className="ml-[20px] pt-[150px] font-[300] font-nunito">
              {`No ${isRestaurant ? "ingredients" : "items"} present. `}
            </p>
          )}
        </div>
      </div>
      <div className="w-[35%] px-[20px] h-full bg-[#f4f4f4] shadow-right relative">
        {Object.keys(restockItems)?.length ? (
          <p className="pt-[20px] font-nunito text-[18px] leading-[27px] text-[#4d4d4d]">
            <TranslateText
              textKey={"review_restock"}
              namespace="manageStocks"
            />
          </p>
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <div className="flex flex-col items-center gap-[20px]">
              <EmptyPreview />
              <p className="font-nunito font-[600] text-[28px] leading-[42px] text-[#d9d9d9]">
                <TranslateText
                  textKey={"restock_details"}
                  namespace="manageStocks"
                />
              </p>
            </div>
          </div>
        )}

        <div className="mt-[30px] max-h-[50%] overflow-y-scroll">
          {Object.values(restockItems)?.map((obj) => {
            const { id, name, addedQuantity } = obj;
            return (
              <div
                key={id}
                className="flex h-[33px] items-center gap-[6px] w-full mb-[10px]"
              >
                <div className="w-full h-[33px] bg-[#fff] px-[11.5px] flex items-center justify-between rounded-[5px]">
                  <span className="font-nunito text-[14px] leading-[18px]">
                    {name}
                  </span>
                  <p className="flex items-center gap-[6px]">
                    <span className="font-nunito text-[14px] leading-[18px]">
                      x
                    </span>
                    <span className="font-nunito text-[14px] leading-[18px] pr-[10px]">
                      {addedQuantity}
                    </span>
                  </p>
                </div>
                <div>
                  <CrossIcon
                    className="cursor-pointer"
                    onClick={() => handleChangeQuantity(id, "delete")}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {Object.keys(restockItems)?.length ? (
          <div className="flex w-full gap-[30px] absolute bottom-[15px] left-0 px-[20px]">
            <button
              className="w-[50%] rounded-[10px] h-[48px] bg-[#fff] flex items-center justify-center cursor-pointer combined-shadows"
              onClick={() => setRestockItems({})}
              disabled={loading}
            >
              <span className="font-[600] text-[18px] leading-[18px] text-[#007FFF]">
                <TranslateText textKey={"clear"} namespace="manageStocks" />
              </span>
            </button>
            <button
              className="w-[50%] rounded-[10px] h-[48px] bg-[#007FFF] flex items-center justify-center cursor-pointer combined-shadows"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress style={{ color: "#ffffff" }} size={22} />
              ) : (
                <span className="font-[600] text-[18px] leading-[18px] text-[#FFF]">
                  <TranslateText textKey={"save"} namespace="manageStocks" />
                </span>
              )}
            </button>
          </div>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default Restock;
